import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { File } from "@ionic-native/file/ngx";
import { Platform } from '@ionic/angular';
import { AndroidPermissions } from '@ionic-native/android-permissions/ngx';
import { FileTransfer, FileTransferObject } from '@ionic-native/file-transfer/ngx';
import { CommonService } from './common.service';
import { DialogService } from './dialog.service';
import { Config } from '../_store';
import { FileOpener } from '@ionic-native/file-opener/ngx';
import { Downloader, DownloadRequest, NotificationVisibility } from '@ionic-native/downloader/ngx';



@Injectable({
    providedIn: 'root'
})
export class DownloadService {
    private fileDownloadMap = {};
    constructor(
        private transfer: FileTransfer,
        private file: File,
        private androidPermissions: AndroidPermissions,
        private platform: Platform,
        private diloagService: DialogService,
        private fileOpener: FileOpener,
        private downloader: Downloader,
    ) {
    }

    downloadFileFromBlob(response, fileName) {
        if (!this.platform.is('cordova')) {
            this.downloadWeb(response, fileName);
            return;
        }
        return this.downloadDevice(response, fileName);
    }

    private downloadWeb(response, fileName) {
        let dataType = response.type;
        let binaryData = [];
        binaryData.push(response);
        let downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));
        downloadLink.setAttribute('download', fileName);
        document.body.appendChild(downloadLink);
        downloadLink.click();
        setTimeout(() => {
            downloadLink.parentNode.removeChild(downloadLink);
        })
    }

    private async downloadDevice(response, fileName) {
        const filePath = await this.getDownloadPath();
        return this.file.writeFile(
            filePath,
            fileName,
            new Blob([response.data]), { replace: true, }).then((data => {
                return this.getReadablePath(data.fullPath);
            }), (err) => {
                this.showAlert(false, fileName, err);
            });
    }

    private isIOS() {
        return this.platform.is('ios');
    }

    public downloadFile(filePath: string, fileName: string, showAlert = true, fileType = '') {
        if (this.isIOS()) {
            showAlert = false;
        }
        return this.performDownload(filePath, fileName, showAlert).then((data: any) => {
            if (this.isIOS()) {
                //file opener
                this.fileOpener.showOpenWithDialog(data.filePath, data.mime);
            } else {
                return data;
            }
        });
    }

    private webDownload(url, fileName) {
        var link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.target = "_blank";
        link.dispatchEvent(new MouseEvent('click'));
    }

    private async performDownload(filePath: string, fileName: string, showAlert = true, filerType = '') {
        if (!this.platform.is('cordova')) {
            this.webDownload(filePath, fileName);
            return;
        }
        return this.isIOS() ? this.performIOSDownload(filePath, fileName, showAlert) : this.performAndroidDownload(filePath, fileName);
    }

    private async performIOSDownload(filePath, fileName, showAlert) {
        const fileTransfer: FileTransferObject = this.transfer.create();
        let uri = encodeURI(filePath);
        let path = await this.getDownloadPath();
        return fileTransfer.download(
            uri,
            path + fileName,
            true
        ).then(
            result => {
                let mimeType = '';
                if (showAlert) {
                    this.showAlert(true, path + fileName, result);
                }
                return { filePath: path + fileName, mime: mimeType, sysPath: result.toURL() };
            },
            error => {
                console.log(fileName, error);
                this.showAlert(false, fileName, error);
                return;
            }
        );
    }

    private async performAndroidDownload(filePath, fileName) {
        const request: DownloadRequest = {
            uri: filePath,
            title: 'PayEX',
            description: '',
            mimeType: '',
            visibleInDownloadsUi: true,
            notificationVisibility: NotificationVisibility.VisibleNotifyCompleted,
            destinationInExternalPublicDir: {
                dirType: 'Download',
                subPath: 'PayEX/' + fileName
            }
        };
        setTimeout(() => {
            if (this.fileDownloadMap[filePath] && this.fileDownloadMap[filePath].downloadStarted) {
                this.diloagService.showMessageDialog('We have queued your download, please check notifications for progress.', { duration: 2000 });
            }
            this.fileDownloadMap[filePath].downloadStarted = false;
        }, 5000);
        if (!this.fileDownloadMap[filePath]) {
            this.fileDownloadMap[filePath] = {};
        }
        this.fileDownloadMap[filePath].downloadStarted = true;
        return this.downloader.download(request)
            .then((location: string) => {
                this.fileDownloadMap[filePath].downloadStarted = false;
                return this.getReadablePath(location);
            })
            .catch((error: any) => {
                this.fileDownloadMap[filePath].downloadStarted = false;
                this.diloagService.hideAllDialogs();
                this.showAlert(false, fileName, '', '');
                throw error;
            });
    }

    private getReadablePath(location) {
        const fileLocationArr = location.split('/');
        let fileLocation = '';
        if (fileLocationArr.length) {
            fileLocation = fileLocationArr.filter(loc => {
                return ['file:', 'storage', 'emulated', '0'].indexOf(loc) === -1 && loc
            }).join('/');
        }
        return {fileLocation, location};
    }

    private getMIMEType(fileName) {
        const extension = fileName.split('.')[1];
        const mimeType = Config.mimeTypeMap[extension] || 'text/plain';
        return mimeType;
    }

    private async getDownloadPath() {
        if (this.isIOS()) {
            return this.file.documentsDirectory;
        }
        //return this.file.dataDirectory;
        await this.androidPermissions.checkPermission(this.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE).then(
            result => {
                if (!result.hasPermission) {
                    return this.androidPermissions.requestPermission(this.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE);
                }
            }
        );
        return this.file.externalRootDirectory + "/Download/PayEX/";
    }

    public showAlert(hasPassed: boolean, fileName: string, error: any = {}, fileType = '') {
        if (!fileType) {
            fileType = 'file';
        }
        let title = hasPassed ? "Download complete!" : "Download failed!";
        let subTitle = hasPassed ? `Your ${fileType} has been downloaded successfully. You can view ${fileType} at ${fileName}.` : `We are sorry, the download has failed due to your device security permissions. Please go to our web-link https://prod.payex.live/ and save document from there.`;
        if (hasPassed) {
            this.diloagService.showInfoAlert(subTitle);
        } else {
            this.diloagService.showErrorModal({
                header: title,
                message: subTitle
            });
        }
    }

    public openFile(path, mimeType) {
        this.fileOpener
            .open(path, mimeType)
            .then(() => console.log("File is opened"))
            .catch(e => console.log("Error opening file", e));
    }
}

